/**
 * Route Manifest - Single source of truth for all application routes.
 *
 * Every route definition includes its component, access policy, and optional
 * navigation metadata. This eliminates the need for runtime find() lookups
 * and keeps routing, access control, and navigation in sync.
 */

import type { AppRoute } from './access/types';
import { lazyWithReload as lazy } from './lazyWithReload';

// ============================================================================
// Shared lazy-loaded components (declared once, used by both solo and shop)
// ============================================================================

const LandingPage = lazy(() =>
  import('../../pages/landing/LandingPage').then(m => ({ default: m.LandingPage })),
);

const ShopsPage = lazy(() =>
  import('../../pages/shops/ShopsPage').then(m => ({ default: m.ShopsPage })),
);

const ShopDetailsPage = lazy(() =>
  import('../../pages/booking/ShopDetailsPage').then(m => ({ default: m.ShopDetailsPage })),
);

const BookingPage = lazy(() =>
  import('../../pages/booking/BookingPage').then(m => ({ default: m.BookingPage })),
);

const GuestTrackingPage = lazy(() =>
  import('../../pages/tracking/GuestTrackingPage').then(m => ({ default: m.GuestTrackingPage })),
);

const BookingConfirmationPage = lazy(() =>
  import('../../pages/booking').then(m => ({ default: m.BookingConfirmationPage })),
);

const ProviderBookingPage = lazy(() =>
  import('../../pages/provider/ProviderBookingPage').then(m => ({ default: m.ProviderBookingPage })),
);

const LoginRedirectPage = lazy(() =>
  import('../../pages/auth').then(m => ({ default: m.LoginRedirectPage })),
);

const RegistrationPage = lazy(() =>
  import('../../pages/registration/RegistrationPage').then(m => ({ default: m.RegistrationPage })),
);

const AcceptPage = lazy(() =>
  import('../../pages/invitation/AcceptPage').then(m => ({ default: m.AcceptPage })),
);

const SuccessPage = lazy(() =>
  import('../../pages/subscription/SuccessPage').then(m => ({ default: m.SuccessPage })),
);

const CheckoutPage = lazy(() =>
  import('../../pages/subscription/CheckoutPage').then(m => ({ default: m.CheckoutPage })),
);

const ReviewSubmitPage = lazy(() =>
  import('../../pages/provider/ReviewSubmitPage').then(m => ({ default: m.ReviewSubmitPage })),
);

const EmergencyOfferPage = lazy(() =>
  import('../../pages/client/EmergencyOfferPage').then(m => ({ default: m.EmergencyOfferPage })),
);

const FeatureDetailPage = lazy(() =>
  import('../../pages/features/FeatureDetailPage').then(m => ({ default: m.FeatureDetailPage })),
);

const TermsPage = lazy(() =>
  import('../../pages/legal/TermsPage').then(m => ({ default: m.TermsPage })),
);

const PrivacyPage = lazy(() =>
  import('../../pages/legal/PrivacyPage').then(m => ({ default: m.PrivacyPage })),
);

const FAQPage = lazy(() =>
  import('../../pages/support/FAQPage').then(m => ({ default: m.FAQPage })),
);

const ContactPage = lazy(() =>
  import('../../pages/support/ContactPage').then(m => ({ default: m.ContactPage })),
);

const SettingsPage = lazy(() =>
  import('../../pages/Settings').then(m => ({ default: m.SettingsPage })),
);

const CallbackPage = lazy(() =>
  import('../../pages/auth/CallbackPage').then(m => ({ default: m.CallbackPage })),
);

const OrganizationPicker = lazy(() =>
  import('../../features/multi-tenancy/OrganizationPicker').then(m => ({
    default: m.OrganizationPicker,
  })),
);

const ShopLoginPage = lazy(() =>
  import('../../pages/auth/ShopLoginPage').then(m => ({ default: m.ShopLoginPage })),
);

const ConnectReturnPage = lazy(() =>
  import('../../pages/stripe/ConnectReturnPage').then(m => ({ default: m.ConnectReturnPage })),
);

const ConnectRefreshPage = lazy(() =>
  import('../../pages/stripe/ConnectRefreshPage').then(m => ({ default: m.ConnectRefreshPage })),
);

const PostLogin = lazy(() =>
  import('../../features/auth/PostLogin').then(m => ({ default: m.PostLogin })),
);

const RoleSelect = lazy(() =>
  import('../../features/onboarding/RoleSelect').then(m => ({ default: m.RoleSelect })),
);

const PageNotFound = lazy(() =>
  import('./404').then(m => ({ default: m.PageNotFound })),
);

const UnauthorizedPage = lazy(() =>
  import('./UnauthorizedPage').then(m => ({ default: m.UnauthorizedPage })),
);

// Provider pages shared between solo and shop portals
const DashboardPage = lazy(() =>
  import('../../pages/provider/DashboardPage').then(m => ({ default: m.DashboardPage })),
);

const AppointmentsPage = lazy(() =>
  import('../../pages/provider/AppointmentsPage').then(m => ({ default: m.AppointmentsPage })),
);

const ScheduleLayout = lazy(() =>
  import('../../pages/provider/ScheduleLayout').then(m => ({ default: m.ScheduleLayout })),
);

const WorkingHoursPage = lazy(() =>
  import('../../pages/provider/WorkingHoursPage').then(m => ({ default: m.WorkingHoursPage })),
);

const TimeOffPage = lazy(() =>
  import('../../pages/provider/TimeOffPage').then(m => ({ default: m.TimeOffPage })),
);

const PendingApprovalsPage = lazy(() =>
  import('../../pages/provider/PendingApprovalsPage').then(m => ({ default: m.PendingApprovalsPage })),
);

const ReviewsPage = lazy(() =>
  import('../../pages/provider/ReviewsPage').then(m => ({ default: m.ReviewsPage })),
);

const MetricsPage = lazy(() =>
  import('../../pages/provider/MetricsPage').then(m => ({ default: m.MetricsPage })),
);

const RevenueDashboard = lazy(() =>
  import('../../features/provider-dashboard/metrics/revenue').then(m => ({
    default: m.RevenueDashboard,
  })),
);

const EarningsPage = lazy(() =>
  import('../../pages/provider/EarningsPage').then(m => ({ default: m.EarningsPage })),
);

const ClientsPage = lazy(() =>
  import('../../pages/provider/ClientsPage').then(m => ({ default: m.ClientsPage })),
);

const ClientDetailPage = lazy(() =>
  import('../../pages/provider/ClientDetailPage').then(m => ({ default: m.ClientDetailPage })),
);

const ServicesPage = lazy(() =>
  import('../../pages/provider/ServicesPage').then(m => ({ default: m.ServicesPage })),
);

const StaffPage = lazy(() =>
  import('../../pages/provider/StaffPage').then(m => ({ default: m.StaffPage })),
);

const ShopSettingsPage = lazy(() =>
  import('../../pages/provider/ShopSettingsPage').then(m => ({ default: m.ShopSettingsPage })),
);

const SoloSettingsPage = lazy(() =>
  import('../../pages/provider/SoloSettingsPage').then(m => ({ default: m.SoloSettingsPage })),
);

const ProviderProfilePage = lazy(() =>
  import('../../pages/provider/ProviderProfilePage').then(m => ({
    default: m.ProviderProfilePage,
  })),
);

const SoloPolicySettingsPage = lazy(() =>
  import('../../pages/provider/SoloPolicySettingsPage').then(m => ({
    default: m.SoloPolicySettingsPage,
  })),
);

const ShopPolicySettingsPage = lazy(() =>
  import('../../pages/provider/PolicySettingsPage').then(m => ({
    default: m.ShopPolicySettingsPage,
  })),
);

const PayoutSettingsPage = lazy(() =>
  import('../../pages/provider/PayoutSettingsPage').then(m => ({
    default: m.PayoutSettingsPage,
  })),
);

const BillingPage = lazy(() =>
  import('../../pages/provider/BillingPage').then(m => ({ default: m.BillingPage })),
);

const SetupPage = lazy(() =>
  import('../../pages/provider/SetupPage').then(m => ({ default: m.SetupPage })),
);

const SetupContinueLaterPage = lazy(() =>
  import('../../pages/provider/SetupContinueLaterPage').then(m => ({ default: m.SetupContinueLaterPage })),
);

const OnboardingPage = lazy(() =>
  import('../../pages/provider/OnboardingPage').then(m => ({ default: m.OnboardingPage })),
);

const IndependentSetupPage = lazy(() =>
  import('../../pages/provider/IndependentSetupPage').then(m => ({
    default: m.IndependentSetupPage,
  })),
);

// Client pages
const ClientDashboardPage = lazy(() =>
  import('../../pages/client/DashboardPage').then(m => ({ default: m.DashboardPage })),
);

const ReschedulePage = lazy(() =>
  import('../../pages/booking/ReschedulePage').then(m => ({ default: m.ReschedulePage })),
);

const ProfilePage = lazy(() =>
  import('../../pages/client/ProfilePage').then(m => ({ default: m.ProfilePage })),
);

const BookingsPage = lazy(() =>
  import('../../pages/client/BookingsPage').then(m => ({ default: m.BookingsPage })),
);

// ============================================================================
// Route Manifest
// ============================================================================

export const manifest: AppRoute[] = [
  // ========== PUBLIC ROUTES ==========
  {
    id: 'public.home',
    path: '/',
    component: LandingPage,
    access: { auth: 'public' },
    title: 'Home',
  },
  // Canonical public discovery and booking routes
  // Uses /discover and /shop (singular) to avoid collision with /shops/:slug workspace routes
  {
    id: 'public.discover',
    path: '/discover',
    component: ShopsPage,
    access: { auth: 'public' },
    title: 'Find Shops',
  },
  {
    id: 'public.shopDetails',
    path: '/shop/:shopId',
    component: ShopDetailsPage,
    access: { auth: 'public' },
    title: 'Shop Details',
  },
  {
    id: 'public.providerBooking',
    path: '/shop/:shopId/providers/:providerId/book',
    component: BookingPage,
    access: { auth: 'public' },
    title: 'Book Appointment',
  },
  // Legacy aliases — keep for backward compatibility with shared links
  /** @deprecated Use public.discover */
  {
    id: 'legacy.barbershops',
    path: '/barbershops',
    component: ShopsPage,
    access: { auth: 'public' },
    title: 'Find Shops',
  },
  /** @deprecated Use public.shopDetails */
  {
    id: 'legacy.shopDetails',
    path: '/barbershops/:shopId',
    component: ShopDetailsPage,
    access: { auth: 'public' },
    title: 'Shop Details',
  },
  /** @deprecated Use public.providerBooking */
  {
    id: 'legacy.providerBooking',
    path: '/barbershops/:shopId/barbers/:providerId/book',
    component: BookingPage,
    access: { auth: 'public' },
    title: 'Book Appointment',
  },
  {
    id: 'public.bookingConfirmation',
    path: '/booking/confirmation',
    component: BookingConfirmationPage,
    access: { auth: 'public' },
    title: 'Booking Confirmation',
  },
  {
    id: 'public.providerDirect',
    path: '/providers/:providerId',
    component: ProviderBookingPage,
    access: { auth: 'public' },
    title: 'Book with Provider',
  },
  // Guest appointment tracking (tokenized link, no auth)
  {
    id: 'public.guestTracking',
    path: '/track/:token',
    component: GuestTrackingPage,
    access: { auth: 'public' },
    title: 'Track Appointment',
  },
  /** @deprecated Use public.providerBooking */
  {
    id: 'legacy.providerBookingAlt',
    path: '/barbershops/:shopId/barber/:barberId',
    component: BookingPage,
    access: { auth: 'public' },
    title: 'Book Appointment',
  },
  {
    id: 'public.login',
    path: '/login',
    component: LoginRedirectPage,
    access: { auth: 'public' },
    title: 'Sign In',
  },
  {
    id: 'public.providerRegister',
    path: '/provider/register',
    component: RegistrationPage,
    access: { auth: 'public' },
    title: 'Provider Registration',
  },
  {
    id: 'public.invitationAccept',
    path: '/provider/invitation/accept',
    component: AcceptPage,
    access: { auth: 'public' },
    title: 'Accept Invitation',
  },
  {
    id: 'public.subscriptionSuccess',
    path: '/subscription/success',
    component: SuccessPage,
    access: { auth: 'public' },
    title: 'Subscription Success',
  },
  {
    id: 'public.subscriptionCheckout',
    path: '/subscription/checkout',
    component: CheckoutPage,
    access: { auth: 'public' },
    title: 'Processing Subscription',
  },
  {
    id: 'public.submitReview',
    path: '/reviews/submit',
    component: ReviewSubmitPage,
    access: { auth: 'public' },
    title: 'Submit Review',
  },
  {
    id: 'public.emergencyOffer',
    path: '/appointments/:appointmentId/emergency-offer',
    component: EmergencyOfferPage,
    access: { auth: 'public' },
    title: 'Emergency Recovery',
  },
  {
    id: 'public.featureDetail',
    path: '/features/:featureSlug',
    component: FeatureDetailPage,
    access: { auth: 'public' },
    title: 'Feature Details',
  },
  {
    id: 'public.terms',
    path: '/terms',
    component: TermsPage,
    access: { auth: 'public' },
    title: 'Terms of Service',
  },
  {
    id: 'public.privacy',
    path: '/privacy',
    component: PrivacyPage,
    access: { auth: 'public' },
    title: 'Privacy Policy',
  },
  {
    id: 'public.faq',
    path: '/faq',
    component: FAQPage,
    access: { auth: 'public' },
    title: 'Frequently Asked Questions',
  },
  {
    id: 'public.contact',
    path: '/contact',
    component: ContactPage,
    access: { auth: 'public' },
    title: 'Contact Support',
  },
  {
    id: 'public.settings',
    path: '/settings',
    component: SettingsPage,
    access: { auth: 'public' },
    title: 'Profile & Security',
  },
  {
    id: 'public.authCallback',
    path: '/auth/callback',
    component: CallbackPage,
    access: { auth: 'public' },
    title: 'Signing in...',
  },
  {
    id: 'authenticated.selectShop',
    path: '/provider/select-shop',
    component: OrganizationPicker,
    access: { auth: 'authenticated' },
    title: 'Select Shop',
  },
  {
    id: 'public.shopLogin',
    path: '/shops/:shopId/login',
    component: ShopLoginPage,
    access: { auth: 'public' },
    title: 'Shop Login',
  },
  {
    id: 'public.stripeReturn',
    path: '/stripe/return',
    component: ConnectReturnPage,
    access: { auth: 'public' },
    title: 'Payment Setup Complete',
  },
  {
    id: 'public.stripeRefresh',
    path: '/stripe/refresh',
    component: ConnectRefreshPage,
    access: { auth: 'public' },
    title: 'Refresh Payment Setup',
  },

  // ========== POST-LOGIN ROUTE ==========
  {
    id: 'auth.postLogin',
    path: '/post-login',
    component: PostLogin,
    access: { auth: 'public' },
    title: 'Redirecting...',
  },

  // ========== ONBOARDING ROUTES ==========
  {
    id: 'onboarding.roleSelect',
    path: '/onboarding/role-select',
    component: RoleSelect,
    access: { auth: 'authenticated', workspace: 'onboarding' },
    title: 'Choose Your Role',
  },
  {
    id: 'onboarding.soloSetup',
    path: '/onboarding/solo-setup',
    component: IndependentSetupPage,
    access: { auth: 'authenticated', workspace: 'onboarding' },
    title: 'Solo Setup',
  },
  {
    id: 'onboarding.shopSetup',
    path: '/onboarding/shop-setup/*',
    component: SetupPage,
    access: { auth: 'authenticated', workspace: 'onboarding' },
    title: 'Shop Setup',
  },

  // ========== SOLO PORTAL ROUTES ==========
  {
    id: 'solo.dashboard',
    path: '/solo/dashboard',
    component: DashboardPage,
    access: {
      auth: 'authenticated',
      actors: ['independent_provider'],
      workspace: 'solo',
      subscription: 'solo_required',
      setup: 'solo_required',
    },
    title: 'Dashboard',
    nav: {
      section: 'main',
      label: 'Dashboard',
      icon: 'Dashboard',
      order: 1,
      visibleFor: ['independent_provider'],
    },
  },
  {
    id: 'solo.appointments',
    path: '/solo/appointments',
    component: AppointmentsPage,
    access: {
      auth: 'authenticated',
      actors: ['independent_provider'],
      workspace: 'solo',
      subscription: 'solo_required',
      setup: 'solo_required',
    },
    title: 'Appointments',
    nav: {
      section: 'bookings',
      label: 'Appointments',
      icon: 'Group',
      order: 2,
      visibleFor: ['independent_provider'],
      parent: 'Calendar',
    },
  },
  {
    id: 'solo.schedule',
    path: '/solo/schedule',
    component: ScheduleLayout,
    access: {
      auth: 'authenticated',
      actors: ['independent_provider'],
      workspace: 'solo',
      subscription: 'solo_required',
      setup: 'solo_required',
    },
    title: 'Schedule',
    nav: {
      section: 'bookings',
      label: 'Schedule',
      icon: 'AccessTime',
      order: 1,
      visibleFor: ['independent_provider'],
      parent: 'Calendar',
    },
  },
  {
    id: 'solo.workingHours',
    path: '/solo/schedule/working-hours',
    component: WorkingHoursPage,
    access: {
      auth: 'authenticated',
      actors: ['independent_provider'],
      workspace: 'solo',
      subscription: 'solo_required',
      setup: 'solo_required',
    },
    title: 'Working Hours',
  },
  {
    id: 'solo.timeOff',
    path: '/solo/schedule/time-off',
    component: TimeOffPage,
    access: {
      auth: 'authenticated',
      actors: ['independent_provider'],
      workspace: 'solo',
      subscription: 'solo_required',
      setup: 'solo_required',
    },
    title: 'Time Off',
  },
  {
    id: 'solo.clients',
    path: '/solo/clients',
    component: ClientsPage,
    access: {
      auth: 'authenticated',
      actors: ['independent_provider'],
      workspace: 'solo',
      subscription: 'solo_required',
      setup: 'solo_required',
    },
    title: 'Clients',
    nav: {
      section: 'clients',
      label: 'Client List',
      icon: 'Group',
      order: 1,
      visibleFor: ['independent_provider'],
      parent: 'Clients',
    },
  },
  {
    id: 'solo.clientDetail',
    path: '/solo/clients/:clientId',
    component: ClientDetailPage,
    access: {
      auth: 'authenticated',
      actors: ['independent_provider'],
      workspace: 'solo',
      subscription: 'solo_required',
      setup: 'solo_required',
    },
    title: 'Client Details',
  },
  {
    id: 'solo.services',
    path: '/solo/services',
    component: ServicesPage,
    access: {
      auth: 'authenticated',
      actors: ['independent_provider'],
      workspace: 'solo',
      subscription: 'solo_required',
      setup: 'solo_required',
    },
    title: 'Services',
    nav: {
      section: 'services',
      label: 'Service List',
      icon: 'ContentCut',
      order: 1,
      visibleFor: ['independent_provider'],
      parent: 'Services',
    },
  },
  {
    id: 'solo.reviews',
    path: '/solo/reviews',
    component: ReviewsPage,
    access: {
      auth: 'authenticated',
      actors: ['independent_provider'],
      workspace: 'solo',
      subscription: 'solo_required',
      setup: 'solo_required',
    },
    title: 'Reviews',
  },
  {
    id: 'solo.earnings',
    path: '/solo/earnings',
    component: EarningsPage,
    access: {
      auth: 'authenticated',
      actors: ['independent_provider'],
      workspace: 'solo',
      subscription: 'solo_required',
      setup: 'solo_required',
    },
    title: 'Earnings',
    nav: {
      section: 'main',
      label: 'Earnings',
      icon: 'Assessment',
      order: 5,
      visibleFor: ['independent_provider'],
    },
  },
  {
    id: 'solo.settings',
    path: '/solo/settings',
    component: SoloSettingsPage,
    access: {
      auth: 'authenticated',
      actors: ['independent_provider'],
      workspace: 'solo',
      subscription: 'solo_required',
      setup: 'solo_required',
    },
    title: 'Settings',
    nav: {
      section: 'settings',
      label: 'Profile Settings',
      icon: 'Person',
      order: 1,
      visibleFor: ['independent_provider'],
      parent: 'Settings',
    },
  },
  {
    id: 'solo.policies',
    path: '/solo/policies',
    component: SoloPolicySettingsPage,
    access: {
      auth: 'authenticated',
      actors: ['independent_provider'],
      workspace: 'solo',
      subscription: 'solo_required',
      setup: 'solo_required',
    },
    title: 'Booking Policies',
    nav: {
      section: 'settings',
      label: 'Booking Policies',
      icon: 'Gavel',
      order: 2,
      visibleFor: ['independent_provider'],
      parent: 'Settings',
    },
  },
  {
    id: 'solo.payments',
    path: '/solo/payments',
    component: PayoutSettingsPage,
    // subscription: 'not_required' — intentional: needed for Stripe Connect onboarding
    access: {
      auth: 'authenticated',
      actors: ['independent_provider'],
      workspace: 'solo',
      subscription: 'not_required',
      setup: 'not_required',
    },
    title: 'Payout Settings',
    nav: {
      section: 'settings',
      label: 'Payout Settings',
      icon: 'AccountBalance',
      order: 3,
      visibleFor: ['independent_provider'],
      parent: 'Settings',
    },
  },
  {
    id: 'solo.billing',
    path: '/solo/billing',
    component: BillingPage,
    access: {
      auth: 'authenticated',
      actors: ['independent_provider'],
      workspace: 'solo',
      subscription: 'solo_required',
      setup: 'solo_required',
    },
    title: 'Plan & Billing',
    nav: {
      section: 'settings',
      label: 'Plan & Billing',
      icon: 'CreditCard',
      order: 4,
      visibleFor: ['independent_provider'],
      parent: 'Settings',
    },
  },

  // ========== SHOP PORTAL ROUTES ==========
  {
    id: 'shop.dashboard',
    path: '/shops/:slug/dashboard',
    component: DashboardPage,
    access: {
      auth: 'authenticated',
      actors: ['shop_owner', 'shop_manager', 'staff_provider'],
      workspace: 'shop',
      subscription: 'shop_required',
    },
    title: 'Dashboard',
    nav: {
      section: 'main',
      label: 'Dashboard',
      icon: 'Dashboard',
      order: 1,
      visibleFor: ['shop_owner', 'shop_manager', 'staff_provider'],
    },
  },
  {
    id: 'shop.appointments',
    path: '/shops/:slug/appointments',
    component: AppointmentsPage,
    access: {
      auth: 'authenticated',
      actors: ['shop_owner', 'shop_manager', 'staff_provider'],
      workspace: 'shop',
      subscription: 'shop_required',
    },
    title: 'Appointments',
    nav: {
      section: 'bookings',
      label: 'Appointments',
      icon: 'Group',
      order: 1,
      visibleFor: ['shop_owner', 'shop_manager', 'staff_provider'],
      parent: 'Bookings',
    },
  },
  {
    id: 'shop.schedule',
    path: '/shops/:slug/schedule',
    component: ScheduleLayout,
    access: {
      auth: 'authenticated',
      actors: ['shop_owner', 'shop_manager', 'staff_provider'],
      workspace: 'shop',
      subscription: 'shop_required',
    },
    title: 'Schedule',
    nav: {
      section: 'bookings',
      label: 'Availability',
      icon: 'AccessTime',
      order: 2,
      visibleFor: ['shop_owner', 'shop_manager', 'staff_provider'],
      parent: 'Bookings',
    },
  },
  {
    id: 'shop.workingHours',
    path: '/shops/:slug/schedule/working-hours',
    component: WorkingHoursPage,
    access: {
      auth: 'authenticated',
      actors: ['shop_owner', 'shop_manager', 'staff_provider'],
      workspace: 'shop',
      subscription: 'shop_required',
    },
    title: 'Working Hours',
  },
  {
    id: 'shop.timeOff',
    path: '/shops/:slug/schedule/time-off',
    component: TimeOffPage,
    access: {
      auth: 'authenticated',
      actors: ['shop_owner', 'shop_manager', 'staff_provider'],
      workspace: 'shop',
      subscription: 'shop_required',
    },
    title: 'Time Off',
  },
  {
    id: 'shop.pendingApprovals',
    path: '/shops/:slug/schedule/time-off/pending',
    component: PendingApprovalsPage,
    access: {
      auth: 'authenticated',
      actors: ['shop_owner', 'shop_manager'],
      workspace: 'shop',
      subscription: 'shop_required',
    },
    title: 'Pending Approvals',
  },
  {
    id: 'shop.clients',
    path: '/shops/:slug/clients',
    component: ClientsPage,
    access: {
      auth: 'authenticated',
      actors: ['shop_owner', 'shop_manager', 'staff_provider'],
      workspace: 'shop',
      subscription: 'shop_required',
    },
    title: 'Clients',
    nav: {
      section: 'clients',
      label: 'Clients',
      icon: 'Group',
      order: 1,
      visibleFor: ['shop_owner', 'shop_manager', 'staff_provider'],
    },
  },
  {
    id: 'shop.clientDetail',
    path: '/shops/:slug/clients/:clientId',
    component: ClientDetailPage,
    access: {
      auth: 'authenticated',
      actors: ['shop_owner', 'shop_manager', 'staff_provider'],
      workspace: 'shop',
      subscription: 'shop_required',
    },
    title: 'Client Details',
  },
  {
    id: 'shop.services',
    path: '/shops/:slug/services',
    component: ServicesPage,
    access: {
      auth: 'authenticated',
      actors: ['shop_owner', 'shop_manager'],
      workspace: 'shop',
      subscription: 'shop_required',
      shopPermissions: ['manage:services'],
    },
    title: 'Services',
    nav: {
      section: 'services',
      label: 'Services',
      icon: 'ContentCut',
      order: 1,
      visibleFor: ['shop_owner', 'shop_manager'],
    },
  },
  {
    id: 'shop.reviews',
    path: '/shops/:slug/reviews',
    component: ReviewsPage,
    access: {
      auth: 'authenticated',
      actors: ['shop_owner', 'shop_manager'],
      workspace: 'shop',
      subscription: 'shop_required',
    },
    title: 'Reviews',
    nav: {
      section: 'analytics',
      label: 'Reviews',
      icon: 'Star',
      order: 2,
      visibleFor: ['shop_owner', 'shop_manager'],
      parent: 'Analytics',
    },
  },
  {
    id: 'shop.earnings',
    path: '/shops/:slug/earnings',
    component: EarningsPage,
    access: {
      auth: 'authenticated',
      actors: ['staff_provider'],
      workspace: 'shop',
      subscription: 'shop_required',
    },
    title: 'My Earnings',
    nav: {
      section: 'analytics',
      label: 'My Earnings',
      icon: 'AttachMoney',
      order: 1,
      visibleFor: ['staff_provider'],
    },
  },
  {
    id: 'shop.staff',
    path: '/shops/:slug/staff',
    component: StaffPage,
    access: {
      auth: 'authenticated',
      actors: ['shop_owner', 'shop_manager'],
      workspace: 'shop',
      subscription: 'shop_required',
      shopPermissions: ['manage:staff'],
    },
    title: 'Staff Management',
    nav: {
      section: 'business',
      label: 'Staff',
      icon: 'Group',
      order: 2,
      visibleFor: ['shop_owner', 'shop_manager'],
      parent: 'Business',
    },
  },
  {
    id: 'shop.settings',
    path: '/shops/:slug/settings',
    component: ShopSettingsPage,
    access: {
      auth: 'authenticated',
      actors: ['shop_owner', 'shop_manager'],
      workspace: 'shop',
      subscription: 'shop_required',
      shopPermissions: ['manage:settings'],
    },
    title: 'Business Profile',
    nav: {
      section: 'business',
      label: 'Business Profile',
      icon: 'Settings',
      order: 3,
      visibleFor: ['shop_owner', 'shop_manager'],
      parent: 'Business',
    },
  },
  {
    id: 'shop.profile',
    path: '/shops/:slug/profile',
    component: ProviderProfilePage,
    access: {
      auth: 'authenticated',
      actors: ['shop_owner', 'shop_manager', 'staff_provider'],
      workspace: 'shop',
      subscription: 'shop_required',
    },
    title: 'Profile Settings',
    nav: {
      section: 'settings',
      label: 'My Profile',
      icon: 'Person',
      order: 1,
      visibleFor: ['shop_owner', 'shop_manager', 'staff_provider'],
      parent: 'Settings',
    },
  },
  {
    id: 'shop.policies',
    path: '/shops/:slug/policies',
    component: ShopPolicySettingsPage,
    access: {
      auth: 'authenticated',
      actors: ['shop_owner', 'shop_manager'],
      workspace: 'shop',
      subscription: 'shop_required',
    },
    title: 'Booking Policies',
    nav: {
      section: 'business',
      label: 'Booking Policies',
      icon: 'Gavel',
      order: 4,
      visibleFor: ['shop_owner', 'shop_manager'],
      parent: 'Business',
    },
  },
  {
    id: 'shop.payments',
    path: '/shops/:slug/payments',
    component: PayoutSettingsPage,
    // subscription: 'not_required' — intentional: needed for Stripe Connect onboarding
    access: {
      auth: 'authenticated',
      actors: ['shop_owner', 'shop_manager', 'staff_provider'],
      workspace: 'shop',
      subscription: 'not_required',
    },
    title: 'Payout Settings',
    nav: {
      section: 'main',
      label: 'Payout Settings',
      icon: 'AccountBalance',
      order: 4,
      visibleFor: ['staff_provider'],
    },
  },
  {
    id: 'shop.billing',
    path: '/shops/:slug/billing',
    component: BillingPage,
    // subscription: 'not_required' — intentional: billing page must be accessible to fix expired subscriptions
    access: {
      auth: 'authenticated',
      actors: ['shop_owner'],
      workspace: 'shop',
      subscription: 'not_required',
    },
    title: 'Plan & Billing',
    nav: {
      section: 'finance',
      label: 'Plan & Billing',
      icon: 'CreditCard',
      order: 3,
      visibleFor: ['shop_owner'],
      parent: 'Finance',
    },
  },
  {
    id: 'shop.metrics',
    path: '/shops/:slug/metrics',
    component: MetricsPage,
    access: {
      auth: 'authenticated',
      actors: ['shop_owner', 'shop_manager'],
      workspace: 'shop',
      subscription: 'shop_required',
      shopPermissions: ['view:analytics'],
    },
    title: 'Performance Metrics',
    nav: {
      section: 'analytics',
      label: 'Performance',
      icon: 'Assessment',
      order: 1,
      visibleFor: ['shop_owner', 'shop_manager'],
      parent: 'Analytics',
    },
  },
  {
    id: 'shop.revenue',
    path: '/shops/:slug/revenue',
    component: RevenueDashboard,
    access: {
      auth: 'authenticated',
      actors: ['shop_owner', 'shop_manager'],
      workspace: 'shop',
      subscription: 'shop_required',
      shopPermissions: ['view:financials'],
    },
    title: 'Revenue Analytics',
    nav: {
      section: 'finance',
      label: 'Revenue',
      icon: 'TrendingUp',
      order: 1,
      visibleFor: ['shop_owner', 'shop_manager'],
      parent: 'Finance',
    },
  },

  // ========== CLIENT ROUTES ==========
  {
    id: 'client.dashboard',
    path: '/client/dashboard',
    component: ClientDashboardPage,
    access: {
      auth: 'authenticated',
      actors: ['client'],
    },
    title: 'My Dashboard',
    nav: {
      section: 'main',
      label: 'Dashboard',
      icon: 'Dashboard',
      order: 1,
      visibleFor: ['client'],
    },
  },
  {
    id: 'client.reschedule',
    path: '/client/appointments/:appointmentId/reschedule',
    component: ReschedulePage,
    access: {
      auth: 'authenticated',
      actors: ['client'],
    },
    title: 'Reschedule Appointment',
  },
  {
    id: 'client.profile',
    path: '/client/profile',
    component: ProfilePage,
    access: {
      auth: 'authenticated',
      actors: ['client'],
    },
    title: 'My Profile',
  },
  {
    id: 'client.bookings',
    path: '/client/bookings',
    component: BookingsPage,
    access: {
      auth: 'authenticated',
      actors: ['client'],
    },
    title: 'Booking History',
  },

  // ========== PROVIDER TRANSITIONAL ROUTES ==========
  {
    id: 'provider.setup',
    path: '/provider/setup/*',
    component: SetupPage,
    // subscription/setup: 'not_required' — intentional: this IS the setup flow
    access: {
      auth: 'authenticated',
      actors: ['shop_owner', 'independent_provider'],
      subscription: 'not_required',
      setup: 'not_required',
    },
    title: 'Shop Setup',
  },
  {
    id: 'provider.setupContinueLater',
    path: '/provider/setup/continue-later',
    component: SetupContinueLaterPage,
    // setup: 'not_required' — safe landing page when user exits setup early
    access: {
      auth: 'authenticated',
      actors: ['shop_owner', 'independent_provider'],
      subscription: 'not_required',
      setup: 'not_required',
    },
    title: 'Continue Later',
  },
  {
    id: 'provider.staffOnboarding',
    path: '/provider/staff-onboarding',
    component: OnboardingPage,
    // subscription/setup: 'not_required' — intentional: this IS the staff onboarding flow
    access: {
      auth: 'authenticated',
      subscription: 'not_required',
      setup: 'not_required',
    },
    title: 'Staff Onboarding',
  },
  {
    id: 'provider.independentSetup',
    path: '/provider/independent-setup',
    component: IndependentSetupPage,
    // subscription/setup: 'not_required' — intentional: this IS the independent provider setup flow
    access: {
      auth: 'authenticated',
      actors: ['independent_provider'],
      subscription: 'not_required',
      setup: 'not_required',
    },
    title: 'Complete Your Profile',
  },

  // ========== ERROR ROUTES ==========
  {
    id: 'error.notFound',
    path: '/404',
    component: PageNotFound,
    access: { auth: 'public' },
    title: 'Page Not Found',
  },
  {
    id: 'error.unauthorized',
    path: '/unauthorized',
    component: UnauthorizedPage,
    access: { auth: 'public' },
    title: 'Unauthorized',
  },
];

// ============================================================================
// Manifest Query Helpers
// ============================================================================

/** Get all routes for a specific workspace */
export const getRoutesByWorkspace = (workspace: string): AppRoute[] =>
  manifest.filter(r => r.access.workspace === workspace);

/** Get a route by its ID */
export const getRouteById = (id: string): AppRoute | undefined =>
  manifest.find(r => r.id === id);

/** Get all routes that have navigation metadata */
export const getNavRoutes = (): AppRoute[] =>
  manifest.filter(r => r.nav !== undefined);

/** Get nav routes for a specific workspace */
export const getNavRoutesByWorkspace = (workspace: string): AppRoute[] =>
  manifest.filter(r => r.nav !== undefined && r.access.workspace === workspace);
